export type Cabin = "Economy" | "Business";

export type FlightFare = {
  id: string;
  name: string;
  cabin: Cabin;
  price: number;
  memberDiscountPct: number;
  pointsEarn: number;
  baggage: string;
  refundable: boolean;
  seatsLeft?: number;
};

export type MockFlight = {
  id: string;
  airline: string;
  number: string;
  fromCode: string;
  fromCity: string;
  fromAirport: string;
  toCode: string;
  toCity: string;
  toAirport: string;
  depart: string;
  arrive: string;
  duration: string;
  durationMins: number;
  stops: number;
  aircraft: string;
  fares: FlightFare[];
};

export const FLIGHT_CITIES = [
  "Mumbai",
  "Goa",
  "Delhi",
  "Bengaluru",
  "Jaipur",
  "Dubai",
  "Coorg",
];

function fare(
  id: string,
  name: string,
  cabin: Cabin,
  price: number,
  pointsEarn: number,
  extras: Partial<FlightFare> = {},
): FlightFare {
  return {
    id,
    name,
    cabin,
    price,
    memberDiscountPct: cabin === "Business" ? 10 : 8,
    pointsEarn,
    baggage: cabin === "Business" ? "2 × 23 kg checked" : "1 × 15 kg checked",
    refundable: name !== "Saver",
    ...extras,
  };
}

export const MOCK_FLIGHTS: MockFlight[] = [
  {
    id: "flt-1",
    airline: "IndiGo",
    number: "6E 214",
    fromCode: "BOM",
    fromCity: "Mumbai",
    fromAirport: "Chhatrapati Shivaji Maharaj",
    toCode: "GOI",
    toCity: "Goa",
    toAirport: "Dabolim",
    depart: "06:40",
    arrive: "08:00",
    duration: "1h 20m",
    durationMins: 80,
    stops: 0,
    aircraft: "A320",
    fares: [
      fare("flt-1-saver", "Saver", "Economy", 4280, 430, {
        baggage: "Cabin bag only",
        seatsLeft: 4,
      }),
      fare("flt-1-flex", "Flex", "Economy", 6120, 610),
      fare("flt-1-biz", "Business", "Business", 14800, 1480, {
        baggage: "2 × 32 kg checked",
      }),
    ],
  },
  {
    id: "flt-2",
    airline: "Air India",
    number: "AI 665",
    fromCode: "BOM",
    fromCity: "Mumbai",
    fromAirport: "Chhatrapati Shivaji Maharaj",
    toCode: "GOI",
    toCity: "Goa",
    toAirport: "Manohar International",
    depart: "14:10",
    arrive: "15:35",
    duration: "1h 25m",
    durationMins: 85,
    stops: 0,
    aircraft: "A320neo",
    fares: [
      fare("flt-2-saver", "Saver", "Economy", 4690, 470, { seatsLeft: 6 }),
      fare("flt-2-flex", "Flex", "Economy", 6540, 650),
    ],
  },
  {
    id: "flt-3",
    airline: "Vistara",
    number: "UK 994",
    fromCode: "DEL",
    fromCity: "Delhi",
    fromAirport: "Indira Gandhi",
    toCode: "BOM",
    toCity: "Mumbai",
    toAirport: "Chhatrapati Shivaji Maharaj",
    depart: "09:15",
    arrive: "11:25",
    duration: "2h 10m",
    durationMins: 130,
    stops: 0,
    aircraft: "A321",
    fares: [
      fare("flt-3-saver", "Saver", "Economy", 5120, 510),
      fare("flt-3-flex", "Flex", "Economy", 7340, 730),
      fare("flt-3-biz", "Business", "Business", 18600, 1860),
    ],
  },
  {
    id: "flt-4",
    airline: "IndiGo",
    number: "6E 512",
    fromCode: "BLR",
    fromCity: "Bengaluru",
    fromAirport: "Kempegowda",
    toCode: "DEL",
    toCity: "Delhi",
    toAirport: "Indira Gandhi",
    depart: "07:05",
    arrive: "09:50",
    duration: "2h 45m",
    durationMins: 165,
    stops: 0,
    aircraft: "A321neo",
    fares: [
      fare("flt-4-saver", "Saver", "Economy", 4890, 490, { seatsLeft: 3 }),
      fare("flt-4-flex", "Flex", "Economy", 7010, 700),
    ],
  },
  {
    id: "flt-5",
    airline: "Emirates",
    number: "EK 501",
    fromCode: "BOM",
    fromCity: "Mumbai",
    fromAirport: "Chhatrapati Shivaji Maharaj",
    toCode: "DXB",
    toCity: "Dubai",
    toAirport: "Dubai International",
    depart: "04:20",
    arrive: "06:05",
    duration: "3h 15m",
    durationMins: 195,
    stops: 0,
    aircraft: "Boeing 777",
    fares: [
      fare("flt-5-saver", "Saver", "Economy", 18450, 1850),
      fare("flt-5-flex", "Flex", "Economy", 24600, 2460),
      fare("flt-5-biz", "Business", "Business", 62400, 6240, {
        baggage: "2 × 32 kg checked",
      }),
    ],
  },
  {
    id: "flt-6",
    airline: "Air India",
    number: "AI 440",
    fromCode: "DEL",
    fromCity: "Delhi",
    fromAirport: "Indira Gandhi",
    toCode: "JAI",
    toCity: "Jaipur",
    toAirport: "Jaipur International",
    depart: "18:30",
    arrive: "19:25",
    duration: "55m",
    durationMins: 55,
    stops: 0,
    aircraft: "A320",
    fares: [
      fare("flt-6-saver", "Saver", "Economy", 3210, 320, {
        baggage: "Cabin bag only",
      }),
      fare("flt-6-flex", "Flex", "Economy", 4580, 460),
    ],
  },
  {
    id: "flt-7",
    airline: "IndiGo",
    number: "6E 881",
    fromCode: "BLR",
    fromCity: "Bengaluru",
    fromAirport: "Kempegowda",
    toCode: "GOI",
    toCity: "Goa",
    toAirport: "Dabolim",
    depart: "11:00",
    arrive: "15:10",
    duration: "4h 10m",
    durationMins: 250,
    stops: 1,
    aircraft: "A320",
    fares: [
      fare("flt-7-saver", "Saver", "Economy", 6740, 670),
      fare("flt-7-flex", "Flex", "Economy", 8920, 890),
    ],
  },
];

export function getFlightById(id: string) {
  return MOCK_FLIGHTS.find((flight) => flight.id === id) ?? null;
}

export function cheapestFare(flight: MockFlight, cabin: Cabin) {
  const matches = flight.fares.filter((item) => item.cabin === cabin);
  const pool = matches.length ? matches : flight.fares;
  return [...pool].sort((a, b) => a.price - b.price)[0];
}

export function cityMatches(query: string, city: string, code: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    city.toLowerCase().includes(q) ||
    code.toLowerCase() === q ||
    `${city} (${code})`.toLowerCase().includes(q)
  );
}
